import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';
import { IconButton, InputAdornment, Link, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import OutComponent from 'src/out-component';
import GoogleSignInComponent from 'src/pages/google-signin/googleSignIn.component';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { LocalStorage } from 'src/utils/storage/local-storage';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';

const LoginForm = ({ isBrand2ChatDomain }) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [isInitFailed, setInitFailed] = useState(false);

  const { showToast } = useToaster();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    let token = LocalStorage.getItem('token');
    if (token) {
      navigate(CommonHelper.GetDefaultPermissionRoute());
    } else {
      generateFormModelSchema();
    }
  }, []);

  useEffect(() => { }, [email, password, showPassword, formSchema, formValidationErrors]);

  const generateFormModelSchema = () => {
    let fields = [
      ValidationTool.GenerateSchema('email', 'Email', 'EMAIL', true),
      ValidationTool.GenerateSchema('password', 'Password', 'TEXT', true),
    ];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    let loginForm = {
      email: email,
      password: password,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, loginForm);
    if (isValidForm) handleLogin();
    else setFormValidationErrors(formValidationErrors);
  };

  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'email':
        setEmail(value ? value.toLowerCase().trim() : value);
        break;
      case 'password':
        setPassword(value);
        break;
      default:
        break;
    }
  };

  const handleOnBlur = (event) => {
    let fieldName = event.target.name;
    let fieldValue = event.target.value;
    let validationErrors = { ...formValidationErrors };
    let message = ValidationTool.ValidateInput(formSchema, fieldValue, fieldName);
    if (message) validationErrors[fieldName] = message;
    else validationErrors[fieldName] = '';
    setFormValidationErrors(validationErrors);
  };

  const handleLogin = (isDemoLogin = false) => {
    setLoading(!isDemoLogin);
    let loginForm = {
      email: email,
      password: password,
      device_type: isMobile ? CommonHelper.DeviceType.Mobile : CommonHelper.DeviceType.Desktop,
    };
    if (isDemoLogin)
      loginForm = {
        email: 'demo@brand2chat.ai',
        password: 'demo1234',
        device_type: isMobile ? CommonHelper.DeviceType.Mobile : CommonHelper.DeviceType.Desktop,
      };
    let postProps = {
      url: userModuleURL.login,
      body: loginForm,
      successCallback: handleLoginSuccess,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleLoginSuccess = (data, message) => {
    CommonHelper.AppLoginCall(data);
  };

  const failureCallback = (message) => {
    showToast(message, 'e');
    setLoading(false);
    setDemoLoading(false);
  };

  const applyDemoCredential = () => {
    setDemoLoading(true);
    handleLogin(true);
  };

  return (
    <>
      {isInitFailed ? (
        <OutComponent />
      ) : (
        <>
          <form onSubmit={handleLoginClick}>
            <Stack spacing={3}>
              <TextField
                name="email"
                label="Email address"
                placeholder="Enter email address"
                onChange={handleInputChange}
                value={email}
                color={email === '' ? 'error' : 'success'}
                helperText={formValidationErrors?.email}
                onBlur={handleOnBlur}
                InputLabelProps={{ shrink: true }}
                error={formValidationErrors?.email}
              />
              <TextField
                name="password"
                label="Password"
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                onChange={handleInputChange}
                onBlur={handleOnBlur}
                value={password}
                color={password === '' ? 'error' : 'success'}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText={formValidationErrors?.password}
                error={formValidationErrors?.password}
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="end" sx={{ my: 2 }}>
              {/* <Checkbox name="remember" label="Remember me" /> */}
              <Link
                variant="subtitle2"
                underline="hover"
                style={{ cursor: 'pointer' }}
                component={RouterLink}
                to={'/reset-password'}
              >
                Forgot password?
              </Link>
            </Stack>
            <LoadingButton fullWidth size="large" type="submit" loading={loading} variant="contained">
              Login
            </LoadingButton>
            {isBrand2ChatDomain && <> <Stack style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}>
              <LoadingButton
                fullWidth
                size="large"
                loading={demoLoading}
                variant="outlined"
                color="info"
                startIcon={
                  // Use StarIcon for demo, as in Register
                  <span className="MuiButton-startIcon MuiButton-iconSizeMedium">
                    <svg width="24" height="24" fill="currentColor">
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                  </span>
                }
                onClick={applyDemoCredential}
                sx={{
                  borderWidth: 2,
                  borderColor: theme.palette.info.main,
                  fontWeight: 'bold',
                  letterSpacing: 1,
                  backgroundColor: '#e3f2fd',
                  color: theme.palette.info.dark,
                  '&:hover': {
                    backgroundColor: '#bbdefb',
                  },
                }}
              >
                {translate && translate('register.use_demo_account')
                  ? translate('register.use_demo_account')
                  : 'Use Demo Account'}
              </LoadingButton>
            </Stack>
              <Typography
                variant="caption"
                align="center"
                display="block"
                sx={{ mt: 1, color: 'info.main', fontWeight: 500 }}
              >
                Try the app instantly with a demo account!
              </Typography>
              <Stack style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px' }}>
                <GoogleSignInComponent handleLoginSuccess={handleLoginSuccess} googleButtonText="Sign in with Google" />
              </Stack> </>}
          </form>
        </>
      )}
    </>
  );
};
export default LoginForm;
